import React, { useEffect, useState } from "react";
import {
  Box, Typography, TextField, Button, Grid,
  FormControl, InputLabel, Select, MenuItem, OutlinedInput,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";

import {
  fetchCustomerValues, updateCustomerValue,
  fetchCustomerTypes,
  fetchFinancialGoals,
} from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";
import { useAuth } from "../../providers/AuthProvider";

export default function SmCustomerValueEditPage() {
  const { businessDomainId, successFactorId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const { companyId } = useAuth();
  const t = (id) => intl.formatMessage({ id });

  const [successFactorName, setSuccessFactorName] = useState("");
  const [successFactorDescription, setSuccessFactorDescription] = useState("");
  const [selectedCustomerType, setSelectedCustomerType] = useState(null);
  const [selectedNames, setSelectedNames] = useState([]);
  const [sourceIds, setSourceIds] = useState([]);
  const [customerTypes, setCustomerTypes] = useState([]);
  const [financialGoals, setFinancialGoals] = useState([]);
  const [customerValues, setCustomerValues] = useState([]);
  const [fullData, setFullData] = useState(null);

  // 指標は選択された顧客タイプの indicators から取得
  const sourceIndicators = selectedCustomerType?.indicators || [];

  useEffect(() => {
    (async () => {
      try {
        const [goals, ctypes, fgoals] = await Promise.all([
          fetchCustomerValues(businessDomainId),
          fetchCustomerTypes(businessDomainId),
          fetchFinancialGoals(businessDomainId),
        ]);
        setCustomerTypes(Array.isArray(ctypes) ? ctypes : []);
        setFinancialGoals(Array.isArray(fgoals) ? fgoals : []);
        // 自分自身を除いた顧客価値をソース候補に設定
        setCustomerValues((Array.isArray(goals) ? goals : []).filter(
          (g) => String(g.successFactorId) !== String(successFactorId)
        ));
        const found = (Array.isArray(goals) ? goals : []).find(
          (g) => String(g.successFactorId) === String(successFactorId)
        );
        if (found) {
          setFullData(found);
          setSuccessFactorName(found.successFactorName || "");
          setSuccessFactorDescription(found.successFactorDescription || "");
          setSourceIds((found.sources || []).map((s) => s.successFactorId));
          setSelectedNames((found.indicators || []).map((ind) => ind.indicatorName));
          if (found.customerTypeId) {
            const foundType = (Array.isArray(ctypes) ? ctypes : []).find(
              (ct) => String(ct.customerTypeId) === String(found.customerTypeId)
            );
            if (foundType) setSelectedCustomerType(foundType);
          }
        }
      } catch (e) {
        notify.apiError(e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessDomainId, successFactorId]);

  const handleCustomerTypeChange = (e) => {
    const type = customerTypes.find((ct) => ct.customerTypeId === e.target.value);
    setSelectedCustomerType(type || null);
    setSelectedNames([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (sourceIndicators.length > 0 && selectedNames.length === 0) {
      notify.error(t("financialGoal.indicatorRequired"));
      return;
    }
    try {
      const indicators = selectedNames
        .map((name) => sourceIndicators.find((ind) => ind.indicatorName === name))
        .filter(Boolean);
      const allSources = [...financialGoals, ...customerValues];
      const sources = sourceIds
        .map((id) => allSources.find((g) => g.successFactorId === id))
        .filter(Boolean);
      await updateCustomerValue({
        ...fullData,
        successFactorName,
        successFactorDescription,
        customerTypeId: selectedCustomerType?.customerTypeId || "",
        indicators,
        sources,
        businessDomainId,
        companyId,
        successFactorId,
      });
      notify.success(t("message.update.success"));
      navigate(`/design/${businessDomainId}/strategy-map/customer-values/${successFactorId}`);
    } catch (err) {
      notify.apiError(err);
    }
  };

  if (!fullData) return null;

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {t("customerValue.edit.title")}
      </Typography>

      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid size={12}>
            <TextField
              fullWidth required
              label={t("successFactor.name")}
              value={successFactorName}
              onChange={(e) => setSuccessFactorName(e.target.value)}
            />
          </Grid>

          <Grid size={12}>
            <TextField
              fullWidth multiline rows={3}
              label={t("successFactor.description")}
              value={successFactorDescription}
              onChange={(e) => setSuccessFactorDescription(e.target.value)}
            />
          </Grid>

          {customerTypes.length > 0 && (
            <Grid size={12}>
              <FormControl fullWidth>
                <InputLabel>{t("customerType.label")}</InputLabel>
                <Select
                  value={selectedCustomerType?.customerTypeId || ""}
                  label={t("customerType.label")}
                  onChange={handleCustomerTypeChange}
                >
                  {customerTypes.map((ct) => (
                    <MenuItem key={ct.customerTypeId} value={ct.customerTypeId}>
                      {ct.customerTypeName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          <Grid size={12}>
            <FormControl fullWidth>
              <InputLabel>{t("customerValue.sources.label")}</InputLabel>
              <Select
                multiple
                value={sourceIds}
                onChange={(e) => setSourceIds(e.target.value)}
                input={<OutlinedInput label={t("customerValue.sources.label")} />}
              >
                {financialGoals.length > 0 && [
                  <MenuItem key="__fg_hd" disabled sx={{ fontWeight: 700, opacity: 1, color: "text.primary" }}>── {t("financialGoal.list.title")} ──</MenuItem>,
                  ...financialGoals.map((g) => (
                    <MenuItem key={g.successFactorId} value={g.successFactorId}>{g.successFactorName}</MenuItem>
                  )),
                ]}
                {customerValues.length > 0 && [
                  <MenuItem key="__cv_hd" disabled sx={{ fontWeight: 700, opacity: 1, color: "text.primary" }}>── {t("customerValue.list.title")} ──</MenuItem>,
                  ...customerValues.map((g) => (
                    <MenuItem key={g.successFactorId} value={g.successFactorId}>{g.successFactorName}</MenuItem>
                  )),
                ]}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={12}>
            <FormControl fullWidth>
              <InputLabel>{t("financialGoal.selectIndicatorHint")}</InputLabel>
              <Select
                multiple
                value={selectedNames}
                onChange={(e) => setSelectedNames(e.target.value)}
                input={<OutlinedInput label={t("financialGoal.selectIndicatorHint")} />}
                disabled={sourceIndicators.length === 0}
              >
                {sourceIndicators.map((ind) => (
                  <MenuItem key={ind.indicatorName} value={ind.indicatorName}>
                    {ind.indicatorName}（{ind.unit}）
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
          <Button variant="contained" type="submit" disabled={!successFactorName.trim()}>
            {t("common.save")}
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate(`/design/${businessDomainId}/strategy-map/customer-values/${successFactorId}`)}
          >
            {t("common.cancel")}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
